import {
  CallDto,
  ConsentGoalDto,
  NeedDto,
  SuggestedActionDto,
  SuggestedNeedDto,
  SuggestedScheduledEventDto,
  SurveyV2,
  UserDto,
} from '@act/shared/data-transfer-objects';
import TabbedPanel, { TabDefinition } from '@act/toolkit/tabs/tabbedPanel';
import { Box, Typography, useTheme } from '@mui/material';
import { Goal } from 'stories/containers/GoalsTracking/types';
import { LoadingStatus } from 'redux/types/shared-types';
import { useEffect, useMemo, useState } from 'react';
import { Button, LoadingPage } from '@act/storybook';
import { SummaryAndTranscripts } from './SummaryAndTrascripts/SummaryAndTranscripts';
import { LlmSuggestions } from './llmSuggestions';
import { useAppDispatch } from 'redux/store-hooks';
import { callsAppSelectors, getSuggestions } from 'redux/redux';
import { GoalsContent } from './GoalsContent';
import { ConsentDrawer } from '../ConsentDrawer';
import { AddNeedContent } from '@act/react-components/needs/need-detail-dialog/AddNeedContent';
import { SurveyDrawer } from '@act/react-components/dialog-forms/components/SurveyDrawer';
import { NeedDetailContent } from '@act/react-components/needs/need-detail-dialog/NeedDetailContent';
import { NeedsDetailAddAction } from '@act/react-components/needs/need-detail-dialog/NeedsDetailAddAction';
import { SuggestedScheduleEvent } from './SuggestedScheduleEvent';
import { TrackingProvider } from '@act/react-components/tracking';
import { useMobile } from '@act/utilities/mobile-context';

interface PostCallPanelProps {
  selectedMember: UserDto | undefined;
  goals: Goal[];
  goalsLoadingStatus: LoadingStatus;
  selectedNeedId: number | undefined;
  onGoalClick: (goal: Goal) => void;
  setSelectedNeedId: (needId: number | undefined) => void;
  onSaveClick: () => void;
  setSelectedSurvey: (survey: SurveyV2.AssignedSurveyDto | undefined) => void;
  selectedSurvey: SurveyV2.AssignedSurveyDto | undefined;
  selectedConsent: ConsentGoalDto | undefined;
  setSelectedConsent: (consent: ConsentGoalDto | undefined) => void;
  setOpenAddNeed: (open: boolean) => void;
  openAddNeed: boolean;
  call: CallDto | undefined;
}

export const PostCallPanel = ({
  selectedMember,
  goals,
  goalsLoadingStatus,
  selectedNeedId,
  onGoalClick,
  onSaveClick,
  setSelectedSurvey,
  selectedSurvey,
  selectedConsent,
  setSelectedConsent,
  setSelectedNeedId,
  setOpenAddNeed,
  openAddNeed,
  call,
}: PostCallPanelProps) => {
  const theme = useTheme();
  const callGoalsPanelProps = {
    goals,
    selectedMember,
    goalsLoadingStatus,
    selectedNeedId,
    onGoalClick,
    setSelectedNeedId,
    onSaveClick,
    setSelectedSurvey,
    selectedSurvey,
    selectedConsent,
    setSelectedConsent,
    setOpenAddNeed,
    openAddNeed,
    call,
  };
  const dispatch = useAppDispatch();
  const { isMobile } = useMobile();
  const [openAddSuggestedNeed, setOpenAddSuggestedNeed] =
    useState<SuggestedNeedDto | null>(null);
  const [openAddAction, setOpenAddAction] = useState<SuggestedActionDto | null>(
    null
  );
  const [openAddScheduledEvent, setOpenAddScheduledEvent] =
    useState<SuggestedScheduledEventDto | null>(null);
  const suggestions = callsAppSelectors.useSelectSuggestionsByCallId(call?.id);
  const suggestionsLoadingStatus =
    callsAppSelectors.useSelectSuggestionsLoadingStatusByCallId(call?.id);

  const [activeTab, setActiveTab] = useState<string>('llm-suggestions');

  // Animation state management for drawer components
  const [shouldRenderNeed, setShouldRenderNeed] = useState(!!selectedNeedId);
  const [needIdForAnimation, setNeedIdForAnimation] = useState<
    number | undefined
  >(selectedNeedId);
  const [shouldRenderSurvey, setShouldRenderSurvey] = useState(
    !!selectedSurvey
  );
  const [surveyForAnimation, setSurveyForAnimation] = useState<
    SurveyV2.AssignedSurveyDto | undefined
  >(selectedSurvey);
  const [shouldRenderConsent, setShouldRenderConsent] = useState(
    !!selectedConsent
  );
  const [consentForAnimation, setConsentForAnimation] = useState<
    ConsentGoalDto | undefined
  >(selectedConsent);

  useEffect(() => {
    if (selectedNeedId) {
      setShouldRenderNeed(true);
      setNeedIdForAnimation(selectedNeedId);
    }
  }, [selectedNeedId]);

  useEffect(() => {
    if (selectedSurvey) {
      setShouldRenderSurvey(true);
      setSurveyForAnimation(selectedSurvey);
    }
  }, [selectedSurvey]);

  useEffect(() => {
    if (selectedConsent) {
      setShouldRenderConsent(true);
      setConsentForAnimation(selectedConsent);
    }
  }, [selectedConsent]);
  useEffect(() => {
    if (call) {
      dispatch(getSuggestions({ params: { id: call.id } }));
    }
  }, [call]);

  const suggestionsLoaded =
    suggestions?.needs?.length > 0 ||
    suggestions?.actions?.length > 0 ||
    suggestions?.scheduledEvents?.length > 0;

  useEffect(() => {
    if (suggestionsLoadingStatus === 'success' && !suggestionsLoaded) {
      setActiveTab('Summary & Transcript');
    } else {
      setActiveTab('llm-suggestions');
    }
  }, [suggestionsLoadingStatus]);

  const tabDefinitions: TabDefinition[] = useMemo(
    () => [
      {
        key: 'llm-suggestions',
        label: (
          <Box
            sx={{
              paddingLeft: isMobile ? 1 : 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="labelLarge" noWrap>
              Suggested Actions
            </Typography>
          </Box>
        ),
        component: (
          <Box sx={{ paddingLeft: 3, height: '96%', paddingRight: 3 }}>
            <LlmSuggestions
              suggestions={suggestions}
              onSaveClick={onSaveClick}
              member={selectedMember}
              setOpenAddSuggestedNeed={setOpenAddSuggestedNeed}
              setOpenAddAction={setOpenAddAction}
              setOpenAddScheduledEvent={setOpenAddScheduledEvent}
            />
          </Box>
        ),
      },
      {
        key: 'Summary & Transcript',
        label: (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="labelLarge" noWrap>
              Summary & Transcript
            </Typography>
          </Box>
        ),
        component: (
          <SummaryAndTranscripts voiceCallRecording={call?.recording} />
        ),
      },
      {
        key: 'Goals',
        label: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              whiteSpace: 'nowrap',
            }}
          >
            <Typography variant="labelLarge" noWrap>
              Goals
            </Typography>
          </Box>
        ),
        component: (
          <TrackingProvider contextName="goals-post-call">
            <Box sx={{ height: '100%', padding: 3 }}>
              <GoalsContent
                goals={goals}
                goalsLoadingStatus={goalsLoadingStatus}
                selectedMember={selectedMember}
                onGoalClick={onGoalClick}
                setOpenAddNeed={setOpenAddNeed}
              />
            </Box>
          </TrackingProvider>
        ),
      },
    ],
    [callGoalsPanelProps, theme]
  );

  const drawerComponent = useMemo(() => {
    if (shouldRenderNeed && needIdForAnimation) {
      return (
        <NeedDetailContent
          needId={needIdForAnimation}
          open={!!selectedNeedId}
          onClose={() => setSelectedNeedId(undefined)}
          onAnimationComplete={() => {
            setShouldRenderNeed(false);
            setNeedIdForAnimation(undefined);
          }}
        />
      );
    }

    if (shouldRenderSurvey && surveyForAnimation) {
      return (
        <TrackingProvider contextName="survey-drawer-post-call">
          <SurveyDrawer
            survey={surveyForAnimation}
            open={!!selectedSurvey}
            onClose={() => setSelectedSurvey(undefined)}
            onAnimationComplete={() => {
              setShouldRenderSurvey(false);
              setSurveyForAnimation(undefined);
            }}
          />
        </TrackingProvider>
      );
    }

    if (shouldRenderConsent && consentForAnimation) {
      return (
        <TrackingProvider contextName="consent-drawer-post-call">
          <ConsentDrawer
            selectedConsent={consentForAnimation}
            onClose={() => setSelectedConsent(undefined)}
            open={!!selectedConsent}
            onAnimationComplete={() => {
              setShouldRenderConsent(false);
              setConsentForAnimation(undefined);
            }}
          />
        </TrackingProvider>
      );
    }

    if (openAddNeed) {
      return (
        <TrackingProvider contextName="add-need-post-call">
          <AddNeedContent
            open={openAddNeed}
            onClose={() => setOpenAddNeed(false)}
          />
        </TrackingProvider>
      );
    }

    if (openAddSuggestedNeed) {
      return (
        <TrackingProvider contextName="add-suggested-need-post-call">
          <AddNeedContent
            open={!!openAddSuggestedNeed}
            onClose={() => setOpenAddSuggestedNeed(null)}
            suggestedNeed={openAddSuggestedNeed}
          />
        </TrackingProvider>
      );
    }

    if (openAddAction?.need) {
      return (
        <TrackingProvider contextName="add-suggested-action-post-call">
          <NeedsDetailAddAction
            need={openAddAction.need as NeedDto}
            open={!!openAddAction}
            onClose={() => setOpenAddAction(null)}
            suggestedAction={openAddAction}
          />
        </TrackingProvider>
      );
    }

    if (openAddScheduledEvent) {
      return (
        <TrackingProvider contextName="add-suggested-scheduled-event-post-call">
          <SuggestedScheduleEvent
            open={!!openAddScheduledEvent}
            onClose={() => setOpenAddScheduledEvent(null)}
            suggestedEvent={openAddScheduledEvent}
            user={selectedMember}
          />
        </TrackingProvider>
      );
    }

    return null;
  }, [
    shouldRenderNeed,
    needIdForAnimation,
    selectedNeedId,
    shouldRenderSurvey,
    surveyForAnimation,
    selectedSurvey,
    shouldRenderConsent,
    consentForAnimation,
    selectedConsent,
    openAddNeed,
    openAddSuggestedNeed,
    openAddAction,
    openAddScheduledEvent,
    selectedMember,
    setSelectedNeedId,
    setSelectedSurvey,
    setSelectedConsent,
    setOpenAddNeed,
    setOpenAddSuggestedNeed,
    setOpenAddAction,
    setOpenAddScheduledEvent,
  ]);

  if (drawerComponent) {
    return drawerComponent;
  }

  if (suggestionsLoadingStatus === 'loading') {
    return <LoadingPage />;
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 3,
        }}
      >
        <Box
          sx={{
            padding: isMobile ? 1 : 0,
            width: isMobile ? 'auto' : '100%',
            height: '85%',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          <TabbedPanel
            tabDefinitions={tabDefinitions}
            hideBottomBorder
            fullWidth={!isMobile}
            shouldEvenflow={!isMobile}
            gap={1}
            setCurrentTabKey={setActiveTab}
            currentTabKey={activeTab}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
            paddingRight: 4,
            paddingTop: 2,
          }}
        >
          <Button variant="outlined" onClick={onSaveClick}>
            Save & Close
          </Button>
        </Box>
      </Box>
    </>
  );
};
